import { conform, useFieldset, type FieldConfig } from '@conform-to/react'
import { useRef, useEffect, useState } from 'react'
import { z } from 'zod'
import { ErrorList } from '#app/components/forms.tsx'
import { Spacer } from '#app/components/spacer.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import { Label } from '#app/components/ui/label.tsx'
import { dataUriToBlob } from '#app/utils/file.ts'
import { cn } from '#app/utils/misc.tsx'

export const MAX_MB_SIZE = 6
export const MAX_UPLOAD_SIZE = 1024 * 1024 * MAX_MB_SIZE // 8MB

export const ImageFieldsetSchema = z.object({
	file: z.instanceof(File).refine(file => {
		return !file || file.size <= MAX_UPLOAD_SIZE
	}, `File size must be less than ${MAX_MB_SIZE}MB`),
})

export function ImageChooser({
	config,
	label,
	onChange,
	swapImageOnUrl,
}: {
	config: FieldConfig<z.infer<typeof ImageFieldsetSchema>>
	label?: string
	onChange: (file: File) => void
	swapImageOnUrl?: string | null
}) {
	const ref = useRef<HTMLFieldSetElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const fields = useFieldset(ref, config)
	const [previewImage, setPreviewImage] = useState<string | null>(null)

	const width = 'w-full'
	const size = 'h-[30rem] ' + width

	useEffect(() => {
		if (swapImageOnUrl) {
			const file = dataUriToBlob(swapImageOnUrl)
			// Create a DataTransfer object
			const dataTransfer = new DataTransfer()
			dataTransfer.items.add(file)
			// Get the file input DOM element
			const fileInput = inputRef.current
			if (fileInput) {
				// Set the files property
				fileInput.files = dataTransfer.files

				// Dispatch the change event
				const event = new Event('change', { bubbles: true })
				fileInput.dispatchEvent(event)
			}
		}
	}, [swapImageOnUrl])

	return (
		<fieldset
			ref={ref}
			className="h-fit w-full"
			aria-invalid={Boolean(config.errors?.length) || undefined}
			aria-describedby={config.errors?.length ? config.errorId : undefined}
		>
			<div className="flex gap-3">
				<div className={width}>
					{label && <Label htmlFor={fields.file.id}>{label}</Label>}
					<Spacer size="4xs" />
					<span className="text-body-sm opacity-40">
						Upload or drop a photo here, don't worry if the face looks cut off
						here, it's not actally cut off
					</span>
					<div className={cn('relative flex-col', size)}>
						<label
							htmlFor={fields.file.id}
							className={cn('group absolute rounded-lg', width, {
								'bg-accent opacity-40 focus-within:opacity-100 hover:opacity-100':
									!previewImage,
							})}
						>
							<div
								className={cn(
									'absolute z-50 min-h-[32px]  rounded-br-md rounded-tl-md p-2 font-bold',
									fields.file.errors ? 'bg-accent' : '',
								)}
							>
								<ErrorList
									elementClassName="text-md"
									id={fields.file.errorId}
									errors={fields.file.errors}
								/>
							</div>
							{previewImage ? (
								<div className="relative">
									<img
										src={previewImage}
										alt={label ?? ''}
										className={cn(
											'rounded-lg object-contain border-[1px] border-solid border-muted-foreground p-[1px]',
											size,
											'aspect-square',
										)}
									/>
								</div>
							) : (
								<div
									className={cn(
										'flex items-center justify-center rounded-lg border border-muted-foreground text-4xl text-muted-foreground',
										size,
										'aspect-square',
									)}
								>
									<Icon name="plus" />
								</div>
							)}
							<input
								ref={inputRef}
								aria-label="Image"
								className={cn(
									'absolute left-0 top-0 z-0 cursor-pointer opacity-0',
									size,
									'aspect-square',
								)}
								onChange={event => {
									const file = event.target.files?.[0]

									if (file) {
										const reader = new FileReader()
										reader.onloadend = () => {
											setPreviewImage(reader.result as string)
											onChange(file)
										}
										reader.readAsDataURL(file)
									} else {
										setPreviewImage(null)
									}
								}}
								accept="image/*"
								{...conform.input(fields.file, {
									type: 'file',
									ariaAttributes: true,
								})}
							/>
						</label>
					</div>
				</div>
			</div>
			<div className="min-h-[32px] px-4 pb-3 pt-1">
				<ErrorList id={config.errorId} errors={config.errors} />
			</div>
		</fieldset>
	)
}
